import { jsx as _jsx } from "react/jsx-runtime";
/* eslint-disable no-restricted-globals */
import { useConfig } from "@/contexts/cdpConfig";
import { useIsDesktopOrMobile } from "@/hooks";
import { useIsNewBuildProject } from "@/shared/hooks";
import { useIsPreviewMode } from "@/shared/hooks/useIsPreviewMode";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from "react";
// @ts-ignore
export const AdsContext = createContext(null);
/**
 * Context provider for advertising
 */
export const AdsProvider = ({ children, bffData, abVariant, distributionType, }) => {
    const slotsRef = useRef({}); // Store ad slot references
    const [loadedSlots, setLoadedSlots] = useState({}); // Store the loaded slots
    const [isGptInitialized, setGptInitialized] = useState(false); // Tracks if GPT is initialized
    const { userDevice } = useIsDesktopOrMobile(); // Get current device type (desktop or mobile)
    const isProject = useIsNewBuildProject(); // Check if it's a new build project
    const isPreviewMode = useIsPreviewMode();
    // Function to register a slot (called when defining slots)
    const registerSlot = useCallback((slotId, slot) => {
        slotsRef.current[slotId] = slot;
        setLoadedSlots((prev) => (Object.assign(Object.assign({}, prev), { [slotId]: "loading" })));
    }, []);
    // Function to reset the loaded slots state
    const deleteAllSlots = useCallback(() => {
        setLoadedSlots({});
    }, [setLoadedSlots]);
    const displayDynamicAdSlot = useCallback((ad) => {
        window.googletag.cmd.push(() => {
            var _a;
            const slot = (_a = googletag
                .defineSlot(ad.adUnit, ad.size, ad.id)) === null || _a === void 0 ? void 0 : _a.addService(googletag.pubads());
            if (!slot)
                return;
            if (ad.specificTargeting) {
                Object.entries(ad.specificTargeting).forEach(([key, value]) => {
                    slot.setTargeting(key, Array.isArray(value) ? value : String(value));
                });
            }
            window.googletag.display(ad.id);
            window.googletag.pubads().refresh([slot], { changeCorrelator: false });
            // Keep track of this slot
            if (slot) {
                registerSlot(slot.getSlotElementId(), slot);
            }
        });
    }, [registerSlot]);
    /** Function to display a specific ad slot
     *  if the slot has been previously registered
     **/
    const displaySlot = useCallback((slotId) => {
        const slot = slotsRef.current[slotId];
        if (slot) {
            googletag.cmd.push(() => {
                window.googletag.display(slotId);
            });
        }
    }, [slotsRef]);
    /**
     * Add a loaded slot to the state loadedSlots
     * @param slotId
     * @param status
     */
    const setLoadedSlot = useCallback((slotId, status) => {
        setLoadedSlots((prev) => (Object.assign(Object.assign({}, prev), { [slotId]: status })));
    }, []);
    /**
     * "testParam" is a custom AdFactory parameter to target a Test Campaign
     * It is sent if the "testParam" adTest=1 is provided in the URL
     */
    const testParam = "adTest";
    const { search } = useConfig();
    const searchParams = new URLSearchParams(search);
    const isInternalTest = searchParams.get(testParam); // Check if it's an internal test
    const targetingFromBff = bffData === null || bffData === void 0 ? void 0 : bffData.tracking_config;
    // Targeting configuration with test parameter if applicable
    const targeting = useMemo(() => (Object.assign(Object.assign(Object.assign({}, targetingFromBff), (isInternalTest ? { [testParam]: "1" } : {})), { ab_test_variant: abVariant })), [targetingFromBff, isInternalTest, abVariant]);
    const prevDeviceTypeRef = useRef(undefined);
    const [hasDeviceTypeChanged, setHasDeviceTypeChanged] = useState(false);
    // Track device type changes
    useEffect(() => {
        if (!userDevice)
            return;
        if (prevDeviceTypeRef.current && prevDeviceTypeRef.current !== userDevice) {
            setHasDeviceTypeChanged(true);
        }
        prevDeviceTypeRef.current = userDevice;
    }, [userDevice]);
    // @ts-ignore
    const contextValue = useMemo(() => {
        var _a;
        if (!userDevice) {
            return {
                adSlots: [],
                getAdSlot: () => null,
                targeting,
                registerSlot,
                displaySlot,
                isGptInitialized,
                setGptInitialized,
                loadedSlots,
                setLoadedSlot,
                deleteAllSlots,
                hasDeviceTypeChanged: false,
                displayDynamicAdSlot,
                distributionType,
            };
        }
        // Prepare ad slots based on device type
        const slotsByDevice = {
            desktop: { slots_config: [] },
            mobile: { slots_config: [] },
        };
        for (const slot of (bffData === null || bffData === void 0 ? void 0 : bffData.slots_config) || []) {
            const devices = slot.device === "all" || !slot.device
                ? ["desktop", "mobile"]
                : [slot.device];
            for (const device of devices) {
                if (!slotsByDevice[device]) {
                    slotsByDevice[device] = { slots_config: [] };
                }
                slotsByDevice[device].slots_config.push(slot);
            }
        }
        // Filter slots based on the current device type
        const slots = isProject || isPreviewMode
            ? []
            : (_a = slotsByDevice[userDevice].slots_config) !== null && _a !== void 0 ? _a : [];
        return {
            adSlots: slots,
            getAdSlot: (slotId) => slots.find((slot) => (slot === null || slot === void 0 ? void 0 : slot.id) === slotId),
            targeting,
            registerSlot,
            displaySlot,
            isGptInitialized,
            setGptInitialized,
            loadedSlots,
            setLoadedSlot,
            deleteAllSlots,
            hasDeviceTypeChanged,
            userDevice,
            displayDynamicAdSlot,
            distributionType,
        };
    }, [
        bffData === null || bffData === void 0 ? void 0 : bffData.slots_config,
        isProject,
        userDevice,
        targeting,
        isGptInitialized,
        loadedSlots,
        deleteAllSlots,
        registerSlot,
        displaySlot,
        setGptInitialized,
        setLoadedSlot,
        hasDeviceTypeChanged,
        displayDynamicAdSlot,
        isPreviewMode,
        distributionType,
    ]);
    return (_jsx(AdsContext.Provider, { value: contextValue, children: children }));
};
export const useAdsContext = () => {
    const context = useContext(AdsContext);
    if (context === null) {
        throw new Error("useAdsContext must be used within an AdsContext provider");
    }
    return context;
};
